import { useState, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import StudentCalendarView from "./StudentCalendarView";
import { Calendar } from "lucide-react";

interface Child {
  id: string;
  student_email: string;
  student_name: string;
  grade: string | null;
}

interface ParentCalendarViewProps {
  children: Child[];
}

export default function ParentCalendarView({ children }: ParentCalendarViewProps) {
  const [childrenWithUserId, setChildrenWithUserId] = useState<Array<Child & { user_id?: string }>>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadChildUserIds();
  }, [children]);

  const loadChildUserIds = async () => {
    try {
      // Get user IDs for all children emails using the database function
      const childrenWithIds = await Promise.all(
        children.map(async (child) => {
          try {
            const { data, error } = await supabase.rpc('get_user_id_from_email', {
              _email: child.student_email
            });

            if (error) {
              console.error('Error fetching user ID for', child.student_email, error);
              return { ...child, user_id: undefined };
            }

            return { ...child, user_id: data || undefined };
          } catch (error) {
            console.error('Error fetching user ID:', error);
            return { ...child, user_id: undefined };
          }
        })
      );

      setChildrenWithUserId(childrenWithIds);
    } catch (error) {
      console.error('Error loading child user IDs:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <Card>
        <CardContent className="pt-6">
          <div className="text-center text-muted-foreground">Loading calendars...</div>
        </CardContent>
      </Card>
    );
  }

  if (children.length === 0) {
    return (
      <Card>
        <CardHeader>
          <div className="flex items-center gap-2">
            <Calendar className="h-5 w-5" />
            <CardTitle>Children's Calendars</CardTitle>
          </div>
          <CardDescription>View and manage calendar events for your children</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="text-center py-8 text-muted-foreground">
            <Calendar className="h-12 w-12 mx-auto mb-4 opacity-50" />
            <p>Add children to view their calendars</p>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center gap-2">
          <Calendar className="h-5 w-5" />
          <CardTitle>Children's Calendars</CardTitle>
        </div>
        <CardDescription>View and manage calendar events for your children</CardDescription>
      </CardHeader>
      <CardContent>
        <Tabs defaultValue={children[0]?.id} className="w-full">
          <TabsList className="grid w-full" style={{ gridTemplateColumns: `repeat(${Math.min(children.length, 4)}, 1fr)` }}>
            {children.slice(0, 4).map((child) => (
              <TabsTrigger key={child.id} value={child.id}>
                {child.student_name.split(' ')[0]}
              </TabsTrigger>
            ))}
          </TabsList>
          {children.map((child) => {
            const childWithUserId = childrenWithUserId.find(c => c.id === child.id);
            return (
              <TabsContent key={child.id} value={child.id}>
                {childWithUserId?.user_id ? (
                  <StudentCalendarView 
                    studentUserId={childWithUserId.user_id}
                    studentName={child.student_name}
                  />
                ) : (
                  <div className="text-center py-8 text-muted-foreground">
                    <p>This child hasn't signed up yet. Calendar will be available once they create an account.</p>
                  </div>
                )}
              </TabsContent>
            );
          })}
        </Tabs>
      </CardContent>
    </Card>
  );
}
